import axios from 'axios';
import { BrowserWhisper } from 'browser-whisper';

// Map internal NLLB-style codes to Whisper ISO codes.
const WHISPER_LANG_MAP = {
  eng_Latn: 'en',
  hin_Deva: 'hi',
  tel_Telu: 'te',
  tam_Taml: 'ta',
  kan_Knda: 'kn',
  mal_Mlym: 'ml',
  mar_Deva: 'mr',
  ben_Beng: 'bn',
  guj_Gujr: 'gu',
  pan_Guru: 'pa',
  urd_Arab: 'ur',
  arb_Arab: 'ar',
  msa_Latn: 'ms',
  ind_Latn: 'id',
  fil_Latn: 'tl',
  spa_Latn: 'es',
  fra_Latn: 'fr',
  deu_Latn: 'de',
  zho_Hans: 'zh',
  jpn_Jpan: 'ja',
  kor_Hang: 'ko',
  rus_Cyrl: 'ru',
  por_Latn: 'pt',
  ita_Latn: 'it',
  nld_Latn: 'nl',
  tur_Latn: 'tr',
  vie_Latn: 'vi',
  tha_Thai: 'th',
};

const FALLBACK_MODEL_KEY = 'wa_transcribe_fallback_model';

let whisperInstance = null;
let currentModel = null;
let currentLang = null;

function getFallbackModel() {
  try {
    const saved = localStorage.getItem(FALLBACK_MODEL_KEY);
    if (saved) return saved;
  } catch {
    // ignore
  }
  return 'whisper-base';
}

function getWhisper(modelName = getFallbackModel(), languageCode = 'en') {
  const lang = getWhisperLanguageCode(languageCode);
  if (!whisperInstance || currentModel !== modelName || currentLang !== lang) {
    whisperInstance = new BrowserWhisper({ model: modelName, language: lang });
    currentModel = modelName;
    currentLang = lang;
  }
  return whisperInstance;
}

export function getWhisperLanguageCode(internalCode) {
  return WHISPER_LANG_MAP[internalCode] || internalCode || 'en';
}

/**
 * Parse a media URL from the WhatsApp media proxy into a mediaId and auth query.
 * Example: /api/whatsapp/media/abc123?phone_number_id=123
 */
function parseMediaUrl(audioUrl) {
  try {
    const url = new URL(audioUrl, window.location.origin);
    const match = url.pathname.match(/\/api\/whatsapp\/media\/([^/]+)$/);
    if (!match) return null;
    return { mediaId: match[1] };
  } catch {
    return null;
  }
}

/**
 * Server-side transcription via OpenAI Whisper (best quality, fast, no browser model download).
 */
async function transcribeOnServer(audioUrl, languageCode) {
  const parsed = parseMediaUrl(audioUrl);
  if (!parsed) {
    throw new Error('Invalid WhatsApp media URL');
  }
  const { data } = await axios.post('/api/whatsapp/transcribe', {
    mediaId: parsed.mediaId,
    language: languageCode,
  });
  return {
    transcript: String(data.transcript || '').trim(),
    source: data.source || 'server',
  };
}

/**
 * Client-side transcription via browser-whisper (fallback when no server API key).
 */
async function transcribeInBrowser(audioUrl, languageCode) {
  const res = await fetch(audioUrl);
  if (!res.ok) throw new Error(`Audio download failed: ${res.status}`);
  const blob = await res.blob();
  const file = new File([blob], 'voice-message.webm', { type: blob.type || 'audio/webm' });
  const whisper = getWhisper(getFallbackModel(), languageCode);
  const lang = getWhisperLanguageCode(languageCode);
  const parts = [];
  for await (const chunk of whisper.transcribe(file, { language: lang })) {
    if (chunk?.text) parts.push(chunk.text);
  }
  return {
    transcript: parts.join(' ').trim(),
    source: `browser-${currentModel || getFallbackModel()}`,
  };
}

/**
 * Transcribe a WhatsApp audio URL.
 * Tries server-side OpenAI Whisper first; falls back to browser-whisper if the server
 * is not configured or the request fails.
 *
 * Returns { transcript: string, source: string }
 */
export async function transcribeAudioUrl(audioUrl, languageCode = 'en') {
  // Try server first for best quality.
  try {
    const result = await transcribeOnServer(audioUrl, languageCode);
    if (result.transcript) return result;
  } catch (err) {
    const code = err?.response?.data?.code || err?.code;
    const status = err?.response?.status;
    // 503 NOT_CONFIGURED means no OPENAI_API_KEY — fall back to browser.
    if (status === 503 || code === 'NOT_CONFIGURED') {
      console.log('[transcribe] Server transcription unavailable, falling back to browser-whisper');
    } else {
      console.warn('[transcribe] Server transcription failed:', err.message);
    }
  }

  return transcribeInBrowser(audioUrl, languageCode);
}

/**
 * Set the client-side fallback Whisper model (used only when server transcription
 * is unavailable). Available options from browser-whisper:
 *   whisper-tiny, whisper-base, whisper-small, whisper-large-v3-turbo
 */
export function setFallbackTranscriptionModel(modelName) {
  try {
    localStorage.setItem(FALLBACK_MODEL_KEY, modelName);
  } catch {
    // ignore
  }
  if (whisperInstance) {
    whisperInstance = null;
    currentModel = null;
    currentLang = null;
  }
}

export function getFallbackTranscriptionModel() {
  return getFallbackModel();
}
